import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  FormField,
  Input,
  Row,
  Box,
  Column,
  Theme,
  CreditCardType,
} from '@chargify/custom-ui-components';

import { CreditCard, CardSize } from '../../components';
import {
  PaymentsFormConfig,
  CreditCardFields,
  DetectedCardType,
} from '../../types';
import { usePaymentsForm } from '../../hooks';

import {
  createFieldsConfig,
  createFieldsStyles,
  mapDetectedCreditCardType,
} from './utils';

import { defaultTranslations } from './translations';
import { CreditCardFormTranslations } from './types';

export type CreditCardFormProps = {
  /**
   * Payments form configuration
   */
  config: PaymentsFormConfig;
  /**
   * Payment method fields values
   */
  values: CreditCardFields;
  /**
   * Required fields configuration
   */
  requiredFields?: string[];
  /**
   * Update credit card field event handler
   */
  onChange: (name: keyof CreditCardFields, value: string) => void;
  /**
   * Validate fields
   */
  onValidateField: (name: keyof CreditCardFields, value: string) => void;
  /**
   * Credit card size
   */
  creditCardSize: CardSize;
  /**
   * Fields errors
   */
  errors?: {
    [key in keyof CreditCardFields]?: 'required' | 'invalid';
  };
  /**
   * PCI fields errors
   */
  hostedFieldsErrors?: string[];
  /**
   * Use 3DS for collecting payment method
   */
  useThreeDSecure?: boolean;
  /**
   * Translations
   */
  translations?: CreditCardFormTranslations;
  /**
   * Mobile device indicator
   */
  isOnMobileDevice?: boolean;
};

const CreditCardForm = ({
  values,
  onValidateField,
  onChange,
  useThreeDSecure,
  errors,
  config,
  creditCardSize,
  isOnMobileDevice,
  requiredFields = [],
  hostedFieldsErrors = [],
  translations = defaultTranslations,
}: CreditCardFormProps) => {
  const paymentsForm = usePaymentsForm();
  const theme = useTheme() as Theme;

  const { firstName, lastName } = values;
  const handleErrorMessage = (error: 'required' | 'invalid') => {
    if (error === 'required') return translations.error.required;

    return translations.error.invalid;
  };

  const [type, setType] = useState<CreditCardType>();

  useEffect(() => {
    const { securityToken, publicKey, serverHost } = config;
    const { globalStyles, fieldsStyles } = createFieldsStyles(theme);

    paymentsForm.load(
      {
        type: 'card',
        publicKey,
        serverHost,
        securityToken,
        threeDSecure: useThreeDSecure,
        hideCardImage: true,
        fields: createFieldsConfig(requiredFields, translations, fieldsStyles),
        style: globalStyles,
      },
      {
        onCardTypeDetected: (cardType: DetectedCardType) =>
          setType(mapDetectedCreditCardType(cardType)),
      }
    );

    return () => paymentsForm.unload();
  }, []);

  const { creditCardNumber, expirationDate, securityCode, tooltip } =
    translations;

  return (
    <div>
      <Box marginBottom={7}>
        <CreditCard
          type={type}
          size={creditCardSize}
          errors={hostedFieldsErrors}
          requiredFields={requiredFields}
          labels={{
            cardNumber: creditCardNumber.label,
            securityCode: securityCode.label,
            expirationDate: expirationDate.label,
            threeDigitTooltip: tooltip.threeDigit,
            fourDigitTooltip: tooltip.fourDigit,
          }}
        />
      </Box>
      <Row rowGap={16}>
        <Column span={isOnMobileDevice ? 24 : 12}>
          <FormField
            label={translations.firstName.label}
            name="firstName"
            hasError={!!errors.firstName}
            required={requiredFields.includes('firstName')}
            error={handleErrorMessage(errors.firstName)}
            fullWidth
          >
            <Input
              name="firstName"
              id="firstName"
              autoComplete="off"
              fullWidth
              value={firstName}
              placeholder={translations.firstName.placeholder}
              hasError={!!errors.firstName}
              onBlur={() => onValidateField('firstName', firstName)}
              onChange={(e) => onChange('firstName', e.currentTarget.value)}
            />
          </FormField>
          <input
            type="hidden"
            data-testid="firstNameMeta"
            data-chargify="firstName"
            value={firstName}
          />
        </Column>
        <Column span={isOnMobileDevice ? 24 : 12}>
          <FormField
            label={translations.lastName.label}
            name="lastName"
            hasError={!!errors.lastName}
            required={requiredFields.includes('lastName')}
            error={handleErrorMessage(errors.lastName)}
            fullWidth
          >
            <Input
              name="lastName"
              id="lastName"
              autoComplete="off"
              fullWidth
              value={lastName}
              placeholder={translations.lastName.placeholder}
              hasError={!!errors.lastName}
              onBlur={() => onValidateField('lastName', lastName)}
              onChange={(e) => onChange('lastName', e.currentTarget.value)}
            />
          </FormField>
          <input
            type="hidden"
            data-testid="lastNameMeta"
            data-chargify="lastName"
            value={lastName}
          />
        </Column>
      </Row>
    </div>
  );
};

export default CreditCardForm;
